import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// ---------------------------------------------------------------------------
// CI "Cleaned data" toggle.
// When ON (the default), CI admin requests get ?clean=1 appended, and the
// backend hides test entities (Zero Carbon / FO1 / test sites) and history
// before the June-2026 cutoff. When OFF, everything is returned ("All data").
//
// A single axios request interceptor appends the flag to CI-ish endpoints, so
// individual pages don't each have to thread the param. Appending clean=1 to an
// endpoint that doesn't understand it is harmless (the param is ignored), but we
// still scope it to CI paths to leave unrelated requests untouched.
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'ci_clean_view';

// Mirror of the backend utils/cleanFilter.js constant. Cleaned mode hides
// everything dated before this. Kept here so date-based UI (e.g. the dashboard's
// overdue window) can clamp to it too.
export const CLEAN_DATA_CUTOFF = '2026-06-01';

// Endpoint path fragments the cleaned view applies to.
const CI_MARKERS = [
  '/commercial-clients',
  '/commercial-sites',
  '/commercial-sld',
  '/schedule',
  '/ci-',
  '/cifo',
  '/job-orders/ops-job-orders',
  '/attendance',
  '/petty-cash',
  '/purchase',
  '/evaluation',
  '/daily',
];

const readInitial = () => {
  const v = localStorage.getItem(STORAGE_KEY);
  return v === null ? true : v === '1'; // default = Cleaned ON
};

// Module-level mirror so the (once-installed) interceptor always sees the live
// value without closure staleness.
let cleanOn = readInitial();
let interceptorInstalled = false;

const installInterceptor = () => {
  if (interceptorInstalled) return;
  interceptorInstalled = true;
  axios.interceptors.request.use((config) => {
    try {
      const url = config?.url || '';
      if (cleanOn && CI_MARKERS.some((m) => url.includes(m))) {
        config.params = { ...(config.params || {}), clean: 1 };
      }
    } catch (_) {
      /* never block a request over this */
    }
    return config;
  });
};

// Install at module load — BEFORE any component mounts and fetches. (A useEffect
// here would run after child effects, so an early fetch could miss ?clean=1.)
installInterceptor();

const CleanDataContext = createContext({ clean: true, setClean: () => {}, toggle: () => {} });

export const CleanDataProvider = ({ children }) => {
  const [clean, setCleanState] = useState(readInitial);

  useEffect(() => { installInterceptor(); }, []); // idempotent safety no-op

  const setClean = (val) => {
    const b = !!val;
    cleanOn = b;                                   // update interceptor mirror first
    try { localStorage.setItem(STORAGE_KEY, b ? '1' : '0'); } catch (_) {}
    setCleanState(b);
  };

  return (
    <CleanDataContext.Provider value={{ clean, setClean, toggle: () => setClean(!clean) }}>
      {children}
    </CleanDataContext.Provider>
  );
};

export const useCleanData = () => useContext(CleanDataContext);
export default CleanDataContext;
